import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColDef,
  ColumnState,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  CellClickedEvent,
  Theme,
  FilterChangedEvent,
  FilterModel,
} from 'ag-grid-community';
import { Sheet } from '@/components/atoms/sheet';
import {
  CollectionErrorState,
  DataTableDrawerSheet,
  DataTablePaginationFooter,
} from '@/components/molecules';
import { DataTableColumnVisibilityList } from '@/components/organisms/DataTableColumnVisibilityList';
import { DataTableToolbar } from '@/components/organisms/DataTableToolbar';
import { dataGridTheme } from './dataGridTheme';
import { DataGridLoaderRow } from './DataGridLoaderRow';
import { isLoaderRow, loaderRows, type LoaderRow } from './loaderRows';

/** Ask for the next page this many rows before the end, so it is usually
 *  there by the time the reader reaches it. */
const LOAD_AHEAD_ROWS = 10;
const INITIAL_SHIMMER_ROWS = 8;
const MORE_SHIMMER_ROWS = 3;

const ACTIONS_COL_ID = '__actions__';

export type DataGridSortDir = 'asc' | 'desc';

export interface DataGridColumn<TKey extends string = string> {
  key: TKey;
  label: string;
  width?: number;
  minWidth?: number;
  flex?: number;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  pinned?: 'left' | 'right';
  hide?: boolean;
  /** Interactive column (e.g. inline editors) — clicking its cells must not fire onRowClick. */
  disableRowClick?: boolean;
  /** AG Grid's native provided filter. The model can still be executed server-side. */
  filterKind?: 'text' | 'number' | 'date';
  filterOptions?: string[];
  filterPlaceholder?: string;
}

/**
 * Portable, server-safe column presentation state. Unlike AG Grid's ColumnState,
 * this intentionally excludes sorting, grouping, aggregation and structural
 * columns so it can be persisted as a user preference outside this component.
 */
export interface DataGridColumnLayout<TKey extends string = string> {
  key: TKey;
  hidden: boolean;
  width?: number;
  pinned?: 'left' | 'right' | null;
  flex?: number | null;
}

interface DataGridPagination {
  count: number;
  singular: string;
  plural?: string;
  page: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
}

/**
 * Grow-as-you-scroll paging, in place of the pager: the caller owns the
 * fetching (see `useInfiniteCallLogs`), the grid decides WHEN to ask.
 */
export interface DataGridInfinite {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreFailed: boolean;
  /** Must be safe to call repeatedly — it is called from scroll events. */
  onLoadMore: () => void;
  onRetry: () => void;
  /** Footer text, e.g. "114 calls". */
  countLabel: string;
  /** Shown on the retry row, e.g. "Couldn't load more calls." */
  failedLabel?: string;
  /** A new value scrolls back to the top — pass the query (search, filters). */
  resetKey: string;
}

interface DataGridProps<TRow, TKey extends string> {
  columns: DataGridColumn<TKey>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  /** Returns the rendered cell for a column. Strings get a hover tooltip + clip. */
  renderCell: (key: TKey, row: TRow) => ReactNode;
  renderRowActions?: (row: TRow) => ReactNode;
  onRowClick?: (row: TRow) => void;
  /** Optional metric/column-aware click callback, fired before onRowClick. */
  onCellClick?: (key: TKey, row: TRow) => void;
  /** Optional full-width rows used for inline expandable detail panels. */
  isFullWidthRow?: (row: TRow) => boolean;
  renderFullWidthRow?: (row: TRow) => ReactNode;
  getRowHeight?: (row: TRow) => number | undefined;
  /** Width of the pinned-right actions column (default 140). */
  actionsColumnWidth?: number;
  /** Optional label for the pinned-right actions column. */
  actionsHeaderLabel?: string;
  storageKey: string;
  emptyTitle: string;
  isLoading?: boolean;
  isError?: boolean;
  errorTitle?: string;
  onRetry?: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  showClearAll: boolean;
  onClearAll: () => void;
  activeFilterCount: number;
  /** Content shown at the start of the toolbar when grid search is hidden. */
  toolbarLeading?: ReactNode;
  toolbarActions?: ReactNode;
  /** Externally persisted column presentation. Its array order is the display order. */
  columnLayout?: DataGridColumnLayout<TKey>[];
  /** Emits normalized, portable column presentation after a user change. */
  onColumnLayoutChange?: (layout: DataGridColumnLayout<TKey>[]) => void;
  /** Controlled AG Grid filter model for datasets filtered by the server. */
  filterModel?: FilterModel;
  onFilterModelChange?: (model: FilterModel) => void;
  /** Supplies raw filter values when cells are rendered through renderCell. */
  getFilterValue?: (key: TKey, row: TRow) => unknown;
  /** Keep legacy localStorage persistence for existing callers (default true). */
  persistLayoutLocally?: boolean;
  /** Hide the grid search when query controls live outside this component. */
  showSearch?: boolean;
  /** Hide the grid filter trigger when filters live outside this component. */
  showFilters?: boolean;
  /** Hide the complete toolbar when its controls are placed by the caller. */
  showToolbar?: boolean;
  /** Render AG Grid flush with its parent instead of inside the standard body inset. */
  flush?: boolean;
  /** Controlled column drawer state for callers that place the trigger elsewhere. */
  columnsPanelOpen?: boolean;
  onColumnsPanelOpenChange?: (open: boolean) => void;
  /** Emits the live visible columns (in display order) — for "visible only" export. */
  onVisibleColumnsChange?: (columns: DataGridColumn<TKey>[]) => void;
  filterSheet?: {
    title?: string;
    content: ReactNode;
    onReset: () => void;
    resetDisabled: boolean;
    /**
     * Commit the drafted filters (deferred-apply, NOT-391). Runs before the sheet
     * closes. Omit for lists still on the legacy instant-apply path — Apply then
     * only closes (the controls have already committed on-change).
     */
    onApply?: () => void;
    /** Reseed the draft from the applied filters when the drawer opens (NOT-391). */
    onOpen?: () => void;
  };
  sortKey?: TKey | null;
  sortDir?: DataGridSortDir | null;
  onSortChange?: (key: TKey | null, dir: DataGridSortDir | null) => void;
  /**
   * Header sort cycle. Defaults to AG Grid's 3-state `['asc','desc',null]`.
   * Server-sorted tables (that fall back to a default sort) should pass
   * `['asc','desc']` so the header never shows "unsorted" while the query still sorts.
   */
  sortingOrder?: ('asc' | 'desc' | null)[];
  pagination?: DataGridPagination;
  /** Optional fixed row height override for tables with two-line identity cells. */
  rowHeight?: number;
  /** Bump to ignore stale local column state after table defaults change. */
  persistenceVersion?: string;
  /** Grid theme; defaults to the app-wide {@link dataGridTheme}. */
  theme?: Theme;
  /** Class on the grid's root, for scoped styling of one table. */
  className?: string;
  /** Replaces the grid body when a loaded page has no rows. */
  emptyState?: ReactNode;
  /**
   * Sets `data-more-right` on the root while columns remain to the right of
   * the viewport, so a pinned actions column can draw its scroll edge.
   */
  trackHorizontalOverflow?: boolean;
  /** The row whose details are open: highlighted, and kept in view. */
  activeRowKey?: string | null;
  /** Replaces the pager with grow-as-you-scroll paging and shimmer rows. */
  infinite?: DataGridInfinite;
  /** Controls placed right after the toolbar search. */
  toolbarAfterSearch?: ReactNode;
  toolbarClassName?: string;
  searchClassName?: string;
  /** Enable a multi-row checkbox column (current page) for bulk actions. */
  enableSelection?: boolean;
  /** Controlled selection by row key — persists across page changes (lives in the parent). */
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  /** Optional aggregate/summary rows pinned below the current page. */
  pinnedBottomRows?: TRow[];
}

/**
 * AG Grid Community wrapped behind an API close to {@link FinancialDataTable} so
 * the rest of the tables can migrate mechanically later. The grid renders only
 * the body; search, the filter sheet, the column-visibility drawer and the
 * count/pagination footer are the same design-system chrome the legacy table
 * uses. Pagination, sort and filters stay server-side — the grid is fed only the
 * current page's rows and never sorts/paginates client-side.
 */
export function DataGrid<TRow, TKey extends string>({
  columns,
  rows,
  getRowKey,
  renderCell,
  renderRowActions,
  onRowClick,
  onCellClick,
  isFullWidthRow,
  renderFullWidthRow,
  getRowHeight,
  actionsColumnWidth = 140,
  actionsHeaderLabel = '',
  storageKey,
  emptyTitle,
  isLoading = false,
  isError = false,
  errorTitle = 'Failed to load records',
  onRetry,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showClearAll,
  onClearAll,
  activeFilterCount,
  toolbarLeading,
  toolbarActions,
  columnLayout,
  onColumnLayoutChange,
  filterModel,
  onFilterModelChange,
  getFilterValue,
  persistLayoutLocally = true,
  showSearch = true,
  showFilters = true,
  showToolbar = true,
  flush = false,
  columnsPanelOpen,
  onColumnsPanelOpenChange,
  onVisibleColumnsChange,
  filterSheet,
  sortKey,
  sortDir,
  onSortChange,
  sortingOrder,
  pagination,
  rowHeight,
  persistenceVersion = 'v5',
  enableSelection = false,
  selectedKeys,
  onSelectionChange,
  pinnedBottomRows,
  theme = dataGridTheme,
  className,
  emptyState,
  trackHorizontalOverflow = false,
  infinite,
  activeRowKey = null,
  toolbarAfterSearch,
  toolbarClassName,
  searchClassName,
}: DataGridProps<TRow, TKey>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const gridApiRef = useRef<GridApi<TRow> | null>(null);
  const applyingSortRef = useRef(false);
  const applyingFilterModelRef = useRef(false);
  const lastNativeFilterFingerprintRef = useRef<string | null>(null);
  const applyingColumnLayoutRef = useRef(false);
  const lastExternalLayoutFingerprintRef = useRef<string | null>(null);
  const onColumnLayoutChangeRef = useRef(onColumnLayoutChange);
  onColumnLayoutChangeRef.current = onColumnLayoutChange;
  const onFilterModelChangeRef = useRef(onFilterModelChange);
  onFilterModelChangeRef.current = onFilterModelChange;
  const getFilterValueRef = useRef(getFilterValue);
  getFilterValueRef.current = getFilterValue;
  const usesExternalLayout = columnLayout !== undefined || onColumnLayoutChange !== undefined;
  // External state is authoritative. Never let a stale browser-local blob compete
  // with it, even when a caller forgets to explicitly disable local persistence.
  const shouldPersistLocally = persistLayoutLocally && !usesExternalLayout;
  // Versioned so callers can ignore stale column state after table defaults change.
  const persistKey = `${storageKey}:aggrid:${persistenceVersion}`;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [internalColumnsOpen, setInternalColumnsOpen] = useState(false);
  const columnsOpen = columnsPanelOpen ?? internalColumnsOpen;
  const setColumnsOpen = useCallback(
    (open: boolean) => {
      if (columnsPanelOpen === undefined) setInternalColumnsOpen(open);
      onColumnsPanelOpenChange?.(open);
    },
    [columnsPanelOpen, onColumnsPanelOpenChange],
  );
  // Mirror of the grid's column order/visibility, kept in sync via grid events so
  // the (reused) column-visibility drawer reflects pin/reorder/hide done in-grid.
  const [columnOrder, setColumnOrder] = useState<TKey[]>(() => columns.map((c) => c.key));
  const [visibleKeys, setVisibleKeys] = useState<TKey[]>(() =>
    columns.filter((c) => !c.hide).map((c) => c.key),
  );
  // NOT-390: the Columns drawer edits a local draft (order + visibility) seeded
  // from the live grid state when it opens. Toggle/reorder mutate only the draft;
  // nothing touches the grid until Apply — so the menu stays in sync with itself
  // and the list doesn't reorder/hide behind the Apply button.
  const [draftOrder, setDraftOrder] = useState<TKey[]>([]);
  const [draftVisible, setDraftVisible] = useState<TKey[]>([]);
  // Set when the user clicks Reset in the drawer: Apply must then also clear the
  // grid state the draft doesn't track (width, flex, pinned), not just re-apply
  // order/visibility — otherwise a resized/pinned column survives a Reset.
  const columnsResetRef = useRef(false);

  // Emit the live visible columns (display order) for "visible only" export.
  // The callback is held in a ref and kept OUT of the effect deps: consumers may
  // pass an inline closure (new identity every render, e.g. a `() => setX(c as T)`
  // cast). If it were a dep, emitting would re-render the consumer → new closure →
  // refire the effect → emit again → "Maximum update depth exceeded". Keyed off the
  // actual visible columns only, this fires solely when they truly change.
  const onVisibleColumnsChangeRef = useRef(onVisibleColumnsChange);
  onVisibleColumnsChangeRef.current = onVisibleColumnsChange;
  useEffect(() => {
    const emit = onVisibleColumnsChangeRef.current;
    if (!emit) return;
    const byKey = new Map(columns.map((c) => [c.key, c] as const));
    const visible = columnOrder
      .filter((key) => visibleKeys.includes(key))
      .map((key) => byKey.get(key))
      .filter((c): c is DataGridColumn<TKey> => Boolean(c));
    emit(visible);
  }, [columnOrder, visibleKeys, columns]);

  const hasActions = Boolean(renderRowActions);

  // The grid fills its width with AG Grid's native `flex` layout — deterministic and
  // free of mount-timing (unlike an imperative sizeColumnsToFit). A table can declare
  // its own flex column; otherwise the first data column auto-flexes to absorb the
  // slack while the rest keep their configured widths.
  const hasFlexColumn = columns.some((column) => column.flex != null);
  const autoFlexColumnKey = useMemo(() => {
    if (hasFlexColumn) return null;
    return columns.find((column) => !column.hide && !column.pinned)?.key ?? null;
  }, [columns, hasFlexColumn]);

  // Keep the render callbacks behind refs so the colDefs stay structurally stable
  // across re-renders (consumers pass inline closures). Rebuilding colDefs makes
  // AG Grid re-apply def-level state (hide/order/width) and clobber the user's
  // live column state — the root cause of "show column / reorder resets".
  const renderCellRef = useRef(renderCell);
  renderCellRef.current = renderCell;
  const renderRowActionsRef = useRef(renderRowActions);
  renderRowActionsRef.current = renderRowActions;

  // Render the drawer rows in the DRAFT order so a pending reorder is visible and
  // subsequent drags compute against the order the user actually sees. Falls back
  // to the prop order before the drawer has seeded a draft.
  const columnDrawerColumns = useMemo(() => {
    const byKey = new Map(columns.map((column) => [column.key, column] as const));
    const order = draftOrder.length > 0 ? draftOrder : columns.map((c) => c.key);
    return order
      .map((key) => byKey.get(key))
      .filter((c): c is (typeof columns)[number] => Boolean(c))
      .map((column) => ({ key: column.key, label: column.label }));
  }, [columns, draftOrder]);

  const noRowClickColIds = useMemo(
    () =>
      new Set<string>(
        columns.filter((column) => column.disableRowClick).map((column) => column.key),
      ),
    [columns],
  );

  // Selection state is controlled by the parent so it survives page changes.
  // Guard re-applies so syncing the grid → parent doesn't loop with parent → grid.
  const applyingSelectionRef = useRef(false);
  const selectionConfig = useMemo(
    () =>
      enableSelection
        ? ({
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: false,
          } as const)
        : undefined,
    [enableSelection],
  );

  // AG Grid treats `defaultColDef` as structural column configuration. Passing a
  // fresh object on every controlled-filter render makes it recreate every
  // column, which also destroys the filter popup while the user is typing.
  const defaultColDef = useMemo<ColDef<TRow>>(() => ({ resizable: true, sortable: false }), []);
  const sortingOrderFingerprint = JSON.stringify(sortingOrder ?? []);
  const stableSortingOrderRef = useRef({
    fingerprint: sortingOrderFingerprint,
    value: sortingOrder,
  });
  if (stableSortingOrderRef.current.fingerprint !== sortingOrderFingerprint) {
    stableSortingOrderRef.current = {
      fingerprint: sortingOrderFingerprint,
      value: sortingOrder,
    };
  }
  const stableSortingOrder = stableSortingOrderRef.current.value;

  const applySelectionToGrid = useCallback(
    (api: GridApi<TRow>) => {
      if (!enableSelection || !selectedKeys) return;
      applyingSelectionRef.current = true;
      api.forEachNode((node) => {
        const shouldSelect = node.id ? selectedKeys.has(node.id) : false;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      });
      applyingSelectionRef.current = false;
    },
    [enableSelection, selectedKeys],
  );

  function handleSelectionChanged() {
    if (applyingSelectionRef.current) return;
    const api = gridApiRef.current;
    if (!api || !onSelectionChange) return;
    const ids = api.getSelectedNodes().map((node) => node.id!);
    onSelectionChange(new Set(ids));
  }

  // Re-apply the parent's selection whenever it changes or a new page renders.
  useEffect(() => {
    const api = gridApiRef.current;
    if (api) applySelectionToGrid(api);
  }, [applySelectionToGrid, rows]);

  const defaultColumnState = useMemo<ColumnState[]>(
    () =>
      columns.map((column) => ({
        colId: column.key,
        hide: Boolean(column.hide),
        pinned: column.pinned ?? null,
        width: column.width,
        flex: column.flex ?? (column.key === autoFlexColumnKey ? 1 : null),
      })),
    [columns, autoFlexColumnKey],
  );

  const externalColumnState = useMemo<ColumnState[] | null>(() => {
    if (!columnLayout) return null;
    const definitions = new Map(columns.map((column) => [column.key, column] as const));
    const defaults = new Map(defaultColumnState.map((state) => [state.colId, state] as const));
    const seen = new Set<TKey>();
    const orderedKeys: TKey[] = [];
    for (const item of columnLayout) {
      if (!definitions.has(item.key) || seen.has(item.key)) continue;
      seen.add(item.key);
      orderedKeys.push(item.key);
    }
    for (const column of columns) {
      if (seen.has(column.key)) continue;
      seen.add(column.key);
      orderedKeys.push(column.key);
    }

    const savedByKey = new Map(columnLayout.map((item) => [item.key, item] as const));
    return orderedKeys.map((key) => {
      const saved = savedByKey.get(key);
      const fallback = defaults.get(key)!;
      return {
        colId: key,
        hide: saved?.hidden ?? fallback.hide,
        pinned: saved?.pinned !== undefined ? saved.pinned : fallback.pinned,
        width: saved?.width ?? fallback.width,
        flex: saved?.flex !== undefined ? saved.flex : fallback.flex,
      };
    });
  }, [columnLayout, columns, defaultColumnState]);

  const externalLayoutFingerprint = useMemo(
    () =>
      externalColumnState
        ? JSON.stringify(
            externalColumnState.map(({ colId, hide, pinned, width, flex }) => ({
              colId,
              hide: Boolean(hide),
              pinned: pinned ?? null,
              width: width ?? null,
              flex: flex ?? null,
            })),
          )
        : null,
    [externalColumnState],
  );
  const externalFilterFingerprint = useMemo(() => JSON.stringify(filterModel ?? {}), [filterModel]);

  // Structural defs only — no hide/pinned/order here. Dynamic column state lives
  // solely in the AG Grid column state (seeded on ready, persisted on change).
  const colDefs = useMemo<ColDef<TRow>[]>(() => {
    const dataDefs = columns.map<ColDef<TRow>>((column) => ({
      colId: column.key,
      headerName: column.label,
      sortable: Boolean(column.sortable),
      // Sort is server-side: neutralise AG Grid's client-side reorder of the
      // current page so the indicator drives the query, not the row order.
      comparator: () => 0,
      resizable: true,
      // Layout props are seeded ONCE (initial*) and never re-synced: a MANAGED
      // `width`/`flex` is re-applied on every columnDefs reconcile and reverts the
      // live column state (a restored layout or the user's manual resize) within a
      // frame. `initialWidth`/`initialFlex` seed the value at creation, then the
      // column state wins — the same rule the grid follows for hide/pin.
      //
      // The auto-flex column (first data column, or a table's own flex column) fills
      // the container via native flex; with flex the seed width is ignored, so its
      // configured width becomes the minimum (floor) — it absorbs slack but never
      // shrinks below its design width.
      initialWidth: column.key === autoFlexColumnKey ? undefined : column.width,
      minWidth: column.minWidth ?? (column.key === autoFlexColumnKey ? (column.width ?? 80) : 80),
      initialFlex: column.flex ?? (column.key === autoFlexColumnKey ? 1 : undefined),
      headerClass:
        column.align === 'right'
          ? 'ag-right-aligned-header'
          : column.align === 'center'
            ? 'data-grid-center-header'
            : undefined,
      cellClass:
        column.align === 'right'
          ? 'ag-right-aligned-cell'
          : column.align === 'center'
            ? 'data-grid-center-cell'
            : undefined,
      tooltipValueGetter: (params) => {
        if (!params.data || isLoaderRow(params.data)) return undefined;
        const content = renderCellRef.current(column.key, params.data as TRow);
        return typeof content === 'string' ? content : undefined;
      },
      filter:
        column.filterKind === 'number'
          ? 'agNumberColumnFilter'
          : column.filterKind === 'date'
            ? 'agDateColumnFilter'
            : column.filterKind === 'text'
              ? 'agTextColumnFilter'
              : false,
      // Report filters are edited through the compact, artifact-aligned
      // condition composer. Keep AG Grid's native filter model and header menu,
      // but do not render its dense floating-input row beneath every header.
      floatingFilter: false,
      filterValueGetter: getFilterValueRef.current
        ? (params) =>
            params.data && !isLoaderRow(params.data)
              ? getFilterValueRef.current?.(column.key, params.data)
              : undefined
        : undefined,
      filterParams: column.filterKind
        ? {
            filterOptions: column.filterOptions,
            defaultOption: column.filterOptions?.[0],
            maxNumConditions: 1,
            // Filter the loaded rows immediately while the same model refreshes
            // the complete server-backed dataset behind the stable popup.
            debounceMs: 150,
            trimInput: column.filterKind === 'text',
            browserDatePicker: column.filterKind === 'date',
            filterPlaceholder: column.filterPlaceholder,
          }
        : undefined,
      cellRenderer: (params: ICellRendererParams<TRow>) =>
        params.data && !isLoaderRow(params.data) ? (
          <div
            className={[
              'flex h-full w-full min-w-0 items-center',
              column.align === 'right' ? 'justify-end text-right' : '',
              column.align === 'center' ? 'justify-center text-center' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {renderCellRef.current(column.key, params.data)}
          </div>
        ) : null,
    }));

    if (hasActions) {
      dataDefs.push({
        colId: ACTIONS_COL_ID,
        headerName: actionsHeaderLabel,
        headerClass: actionsHeaderLabel ? 'ag-right-aligned-header' : undefined,
        // Always fixed at the end: pinned right, locked so the user can't move/unpin it.
        pinned: 'right',
        lockPinned: true,
        lockPosition: 'right',
        suppressMovable: true,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        // Keep interactive controls (menus, dialogs) mounted while they handle
        // their own pointer events. Otherwise AG Grid focuses the cell and may
        // refresh its renderer before the control can open.
        cellRendererParams: {
          suppressMouseEventHandling: () => true,
        },
        // Fixed width: pinned-right and never flex, so the data columns absorb the
        // slack and the actions column stays put.
        width: actionsColumnWidth,
        cellRenderer: (params: ICellRendererParams<TRow>) =>
          params.data && !isLoaderRow(params.data) ? (
            <div className="flex h-full items-center justify-end">
              {renderRowActionsRef.current!(params.data)}
            </div>
          ) : null,
      });
    }

    return dataDefs;
  }, [columns, hasActions, actionsColumnWidth, actionsHeaderLabel, autoFlexColumnKey]);
  const persistColumnState = useCallback(
    (api: GridApi<TRow>) => {
      // Drop sort from persistence — it is owned by the server-side query, not
      // the grid, so we never want a stale localStorage sort fighting the props.
      // Also drop the actions column — its pin/position is structural, not
      // user-owned, so a stale blob must never un-pin it.
      const allowedKeys = new Set(columns.map((column) => column.key));
      const state = api
        .getColumnState()
        .filter((c) => c.colId !== ACTIONS_COL_ID && allowedKeys.has(c.colId as TKey))
        .map(({ sort, sortIndex, ...rest }) => {
          void sort;
          void sortIndex;
          return rest;
        });

      const emit = onColumnLayoutChangeRef.current;
      if (emit) {
        emit(
          state.map((item) => ({
            key: item.colId as TKey,
            hidden: Boolean(item.hide),
            width: typeof item.width === 'number' ? item.width : undefined,
            pinned: item.pinned === 'left' || item.pinned === 'right' ? item.pinned : null,
            flex: typeof item.flex === 'number' ? item.flex : null,
          })),
        );
      }

      if (shouldPersistLocally) {
        try {
          window.localStorage.setItem(persistKey, JSON.stringify(state));
        } catch {
          // Storage unavailable (private mode/quota) — non-fatal.
        }
      }
    },
    [columns, persistKey, shouldPersistLocally],
  );

  const syncMirrorFromGrid = useCallback((api: GridApi<TRow>) => {
    const state = api.getColumnState();
    setColumnOrder(state.map((s) => s.colId).filter((id): id is TKey => id !== ACTIONS_COL_ID));
    setVisibleKeys(
      state.filter((s) => !s.hide && s.colId !== ACTIONS_COL_ID).map((s) => s.colId as TKey),
    );
  }, []);

  const applySortToGrid = useCallback(
    (api: GridApi<TRow>) => {
      applyingSortRef.current = true;
      api.applyColumnState({
        state: sortKey ? [{ colId: sortKey, sort: sortDir ?? null, sortIndex: 0 }] : [],
        defaultState: { sort: null, sortIndex: null },
      });
      applyingSortRef.current = false;
    },
    [sortKey, sortDir],
  );

  function handleGridReady(event: GridReadyEvent<TRow>) {
    gridApiRef.current = event.api;
    let saved: string | null = null;
    if (shouldPersistLocally) {
      // Drop the pre-v2 persisted state (had default pins) so it can't linger.
      try {
        window.localStorage.removeItem(`${storageKey}:aggrid`);
        if (persistenceVersion !== 'v2') {
          window.localStorage.removeItem(`${storageKey}:aggrid:v2`);
        }
        saved = window.localStorage.getItem(persistKey);
      } catch {
        // Storage unavailable (private mode/quota) — use the default seed.
      }
    }

    let applied = false;
    applyingColumnLayoutRef.current = true;
    if (externalColumnState) {
      event.api.applyColumnState({ state: externalColumnState, applyOrder: true });
      lastExternalLayoutFingerprintRef.current = externalLayoutFingerprint;
      applied = true;
    } else if (saved) {
      try {
        // Filter the actions column out of any persisted blob so the structural
        // pinned-right def wins over an older state where it was un-pinned.
        const state = (JSON.parse(saved) as ColumnState[]).filter(
          (c) => c.colId !== ACTIONS_COL_ID,
        );
        event.api.applyColumnState({ state, applyOrder: true });
        applied = true;
      } catch {
        // Corrupt state — fall through to the default seed.
      }
    }
    if (!applied) {
      // Seed the complete default layout as one atomic column-state update. AG Grid
      // can otherwise resolve initialFlex for body cells before its header viewport
      // has consumed the initial widths, briefly leaving headers and rows misaligned.
      event.api.applyColumnState({ state: defaultColumnState, applyOrder: true });
    }
    applySortToGrid(event.api);
    applyingFilterModelRef.current = true;
    event.api.setFilterModel(filterModel ?? null);
    applyingFilterModelRef.current = false;
    event.api.refreshHeader();
    event.api.redrawRows();
    syncMirrorFromGrid(event.api);
    applySelectionToGrid(event.api);
    applyingColumnLayoutRef.current = false;
  }

  // ── Infinite paging ────────────────────────────────────────────────────
  // The rows AG Grid draws: the caller's, then any shimmer or retry row. The
  // loader rows are the grid's own and never reach a caller's callback.
  const gridRows = useMemo<Array<TRow | LoaderRow>>(() => {
    if (!infinite) return rows;
    if (isLoading && rows.length === 0) return loaderRows('shimmer', INITIAL_SHIMMER_ROWS);
    if (infinite.isLoadingMore) return [...rows, ...loaderRows('shimmer', MORE_SHIMMER_ROWS)];
    if (infinite.loadMoreFailed && rows.length > 0) return [...rows, ...loaderRows('error', 1)];
    return rows;
  }, [infinite, isLoading, rows]);

  // Read through refs: AG Grid holds on to the handlers it was first given,
  // and a stale `hasMore` would keep asking after the last page.
  const infiniteRef = useRef(infinite);
  infiniteRef.current = infinite;
  const rowCountRef = useRef(rows.length);
  rowCountRef.current = rows.length;

  const maybeLoadMore = useCallback((api: GridApi<TRow> | null) => {
    const paging = infiniteRef.current;
    // Paging first: a grid without infinite paging never touches the api here.
    if (!paging || !api || api.isDestroyed()) return;
    if (!paging.hasMore || paging.isLoadingMore || paging.loadMoreFailed) return;
    const count = rowCountRef.current;
    if (count === 0) return;
    // Also true when the first page does not fill the viewport — then there
    // is no scrollbar to scroll, and this is the only thing that asks.
    if (api.getLastDisplayedRowIndex() >= count - LOAD_AHEAD_ROWS) paging.onLoadMore();
  }, []);

  // A page landing, or a failed page being retried, can leave the reader
  // still at the bottom with nothing new to scroll — ask again then.
  useEffect(() => {
    maybeLoadMore(gridApiRef.current);
  }, [maybeLoadMore, infinite?.hasMore, infinite?.isLoadingMore, infinite?.loadMoreFailed, rows.length]);

  // A different question starts at the top of its answer.
  const resetKey = infinite?.resetKey;
  const lastResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;
    const api = gridApiRef.current;
    if (api && !api.isDestroyed() && api.getDisplayedRowCount() > 0) {
      api.ensureIndexVisible(0, 'top');
    }
  }, [resetKey]);

  // ── The open row ───────────────────────────────────────────────────────
  // Through a ref, because AG Grid evaluates `getRowClass` when it draws a
  // row and keeps the function it was first given.
  const activeRowKeyRef = useRef(activeRowKey);
  activeRowKeyRef.current = activeRowKey;
  const getRowKeyRef = useRef(getRowKey);
  getRowKeyRef.current = getRowKey;
  const previousActiveRef = useRef<string | null>(null);
  useEffect(() => {
    const api = gridApiRef.current;
    const previous = previousActiveRef.current;
    previousActiveRef.current = activeRowKey;
    if (!api || api.isDestroyed()) return;
    const nodes = [previous, activeRowKey]
      .map((key) => (key ? api.getRowNode(key) : undefined))
      .filter((node): node is NonNullable<typeof node> => Boolean(node));
    if (nodes.length > 0) api.redrawRows({ rowNodes: nodes });
    // Stepping through calls with the keyboard walks the list; keep the row
    // being read on screen behind the drawer.
    const current = activeRowKey ? api.getRowNode(activeRowKey) : undefined;
    if (current) api.ensureNodeVisible(current);
  }, [activeRowKey]);

  const [moreRight, setMoreRight] = useState(false);
  const measureOverflow = useCallback(
    (api: GridApi<TRow>) => {
      if (!trackHorizontalOverflow || api.isDestroyed()) return;
      const total = api
        .getDisplayedCenterColumns()
        .reduce((sum, column) => sum + column.getActualWidth(), 0);
      const { right } = api.getHorizontalPixelRange();
      setMoreRight(right < total - 1);
    },
    [trackHorizontalOverflow],
  );

  function handleGridSizeChanged(api: GridApi<TRow>) {
    // View switches can mount the grid while its parent is still resolving from
    // the library split-pane width to the table width. Wait for that layout to
    // settle, then refresh both layers together so header and body use the same
    // column geometry.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (gridApiRef.current !== api) return;
        // Re-commit the live column model after the new container width is known.
        // This forces AG Grid's header and center-row viewports to consume the same
        // resolved flex widths after switching between sibling layouts.
        api.applyColumnState({
          state: api.getColumnState(),
          applyOrder: true,
        });
        api.refreshHeader();
        api.refreshCells({ force: true });
        api.redrawRows();
        measureOverflow(api);
        maybeLoadMore(api);
      });
    });
  }

  // Keep the grid's sort indicator in sync with the server-driven sort props.
  useEffect(() => {
    const api = gridApiRef.current;
    if (api) applySortToGrid(api);
  }, [applySortToGrid]);

  // Keep AG Grid's native filter controls synchronized with the persisted server
  // predicates. The current page is deliberately allowed through unchanged below;
  // the parent re-runs the complete dataset query from this model.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    // A native menu edit has already installed this exact model inside AG Grid.
    // Do not replay the controlled prop back through setFilterModel: during the
    // filter event AG Grid can still expose the previous model for one render,
    // and that redundant API call destroys the menu the user is typing in.
    if (lastNativeFilterFingerprintRef.current === externalFilterFingerprint) {
      lastNativeFilterFingerprintRef.current = null;
      return;
    }
    const currentFingerprint = JSON.stringify(api.getFilterModel() ?? {});
    if (currentFingerprint === externalFilterFingerprint) return;
    applyingFilterModelRef.current = true;
    api.setFilterModel(filterModel ?? null);
    applyingFilterModelRef.current = false;
  }, [externalFilterFingerprint, filterModel]);

  // An external layout can arrive after the grid mounts (for example after the
  // user view query resolves), or be replaced by a reset from the parent. Reapply
  // only when its effective state changes; an echoed onColumnLayoutChange payload
  // therefore does not bounce indefinitely between the grid and its owner.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api || !externalColumnState || !externalLayoutFingerprint) return;
    if (lastExternalLayoutFingerprintRef.current === externalLayoutFingerprint) return;

    applyingColumnLayoutRef.current = true;
    api.applyColumnState({ state: externalColumnState, applyOrder: true });
    applySortToGrid(api);
    api.refreshHeader();
    api.refreshCells({ force: true });
    syncMirrorFromGrid(api);
    lastExternalLayoutFingerprintRef.current = externalLayoutFingerprint;
    applyingColumnLayoutRef.current = false;
  }, [externalColumnState, externalLayoutFingerprint, applySortToGrid, syncMirrorFromGrid]);

  function handleSortChanged() {
    if (applyingSortRef.current) return;
    const api = gridApiRef.current;
    if (!api) return;
    const sorted = api.getColumnState().find((s) => s.sort);
    const nextKey = sorted ? (sorted.colId as TKey) : null;
    const nextDir = (sorted?.sort as DataGridSortDir | undefined) ?? null;
    if ((sortKey ?? null) === nextKey && (sortDir ?? null) === nextDir) return;
    onSortChange?.(nextKey, nextDir);
  }

  function handleFilterChanged(event: FilterChangedEvent<TRow>) {
    if (applyingFilterModelRef.current) return;
    const next = event.api.getFilterModel();
    const nextFingerprint = JSON.stringify(next ?? {});
    if (nextFingerprint === externalFilterFingerprint) return;
    // Native AG Grid filters are intentionally live. Echo the model to the
    // server-backed report on every edit, including an empty model when the user
    // clears the input. The controlled-model effect recognises this fingerprint
    // and does not replay it into AG Grid, so the popup remains mounted while the
    // complete dataset refreshes behind it.
    lastNativeFilterFingerprintRef.current = nextFingerprint;
    onFilterModelChangeRef.current?.(next);
  }

  function handleColumnChanged() {
    if (applyingColumnLayoutRef.current) return;
    const api = gridApiRef.current;
    if (!api) return;
    persistColumnState(api);
    syncMirrorFromGrid(api);
  }

  function handleColumnResized(event: ColumnResizedEvent<TRow>) {
    measureOverflow(event.api);
    // Persist ONLY a real user drag. AG Grid fires onColumnResized for flex
    // redistribution and applyColumnState too (other sources); persisting those would
    // save flex's computed width as a fixed width and kill the flex on the next load.
    // Dragging the flex column clears its flex (AG sets width + flex:null), which we
    // persist here so the user's width wins on reload. Real hide/reorder/pin sync
    // arrives via their own handlers, not via resize.
    if (event.source !== 'uiColumnResized') return;
    handleColumnChanged();
  }

  function handleColumnMoved(event: ColumnMovedEvent<TRow>) {
    // AG Grid emits intermediate move events while the pointer is still dragging.
    // Persisting those through a controlled external layout causes the parent to
    // reapply column state mid-gesture, making the header jump underneath the user.
    if (!event.finished) return;
    handleColumnChanged();
  }

  function handleCellClicked(event: CellClickedEvent<TRow>) {
    const colId = event.colDef.colId;
    if (colId === ACTIONS_COL_ID) return;
    if (isLoaderRow(event.data)) return;
    // A control inside a cell (a play button, a link) owns its own click. AG
    // Grid listens on the cell natively, so React's stopPropagation in the
    // control cannot stop this handler — the play button opened the call's
    // detail panel as well as playing. Ask the DOM what was actually hit.
    const target = event.event?.target;
    if (
      target instanceof Element &&
      target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"]')
    ) {
      return;
    }
    // The auto-generated selection column owns its click (toggles selection).
    if (colId && colId.startsWith('ag-Grid-SelectionColumn')) return;
    // Interactive columns (e.g. inline editors) own their click; don't navigate.
    if (colId && noRowClickColIds.has(colId)) return;
    if (event.data && colId) onCellClick?.(colId as TKey, event.data);
    if (event.data) onRowClick?.(event.data);
  }

  // Seed the draft from the current mirror (which tracks the grid) when the drawer
  // opens, so it always reflects the applied layout — not a stale earlier edit.
  const seedColumnsDraft = useCallback(() => {
    columnsResetRef.current = false;
    setDraftOrder([...columnOrder]);
    setDraftVisible([...visibleKeys]);
  }, [columnOrder, visibleKeys]);

  const previousColumnsOpenRef = useRef(false);
  useEffect(() => {
    if (columnsOpen && !previousColumnsOpenRef.current) seedColumnsDraft();
    previousColumnsOpenRef.current = columnsOpen;
  }, [columnsOpen, seedColumnsDraft]);

  function handleToggleColumn(key: string) {
    setDraftVisible((prev) =>
      prev.includes(key as TKey) ? prev.filter((k) => k !== key) : [...prev, key as TKey],
    );
  }

  function handleColumnsDragEnd(dragEvent: DragEndEvent) {
    const { active, over } = dragEvent;
    if (!over || active.id === over.id) return;
    setDraftOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(active.id as TKey);
      const to = next.indexOf(over.id as TKey);
      if (from === -1 || to === -1) return prev;
      next.splice(to, 0, next.splice(from, 1)[0]!);
      return next;
    });
  }

  // Commit the draft (order + visibility together) to the grid on Apply, then
  // persist + resync the mirror and close.
  function applyColumnsDraft() {
    const api = gridApiRef.current;
    if (api) {
      if (columnsResetRef.current) {
        // Reset was requested: clear the full column state (width, flex, pinned)
        // back to the table defaults before re-applying the draft order/visibility.
        // handleColumnChanged() below then persists the reset-plus-draft state — so
        // edits made after Reset but before Apply survive a reload, and the cleared
        // width/flex/pinned don't come back.
        api.resetColumnState();
        const seed = columns
          .filter((column) => column.hide || column.pinned)
          .map((column) => ({
            colId: column.key,
            hide: Boolean(column.hide),
            pinned: column.pinned ?? null,
          }));
        if (seed.length > 0) api.applyColumnState({ state: seed });
        applySortToGrid(api);
      }
      api.applyColumnState({
        state: draftOrder.map((colId) => ({ colId, hide: !draftVisible.includes(colId) })),
        applyOrder: true,
      });
      handleColumnChanged();
    }
    columnsResetRef.current = false;
    setColumnsOpen(false);
  }

  function handleResetColumns() {
    // Revert the draft to the table's default layout (config order + visibility);
    // it only reaches the grid when the user clicks Apply, consistent with the
    // filter drawer's deferred Reset. The flag makes Apply also clear width/flex/
    // pinned, which the draft doesn't track.
    columnsResetRef.current = true;
    setDraftOrder(columns.map((c) => c.key));
    setDraftVisible(columns.filter((c) => !c.hide).map((c) => c.key));
  }

  return (
    <div
      className={['flex h-full min-h-0 flex-col', className].filter(Boolean).join(' ')}
      data-more-right={trackHorizontalOverflow && moreRight ? '' : undefined}
    >
      {showToolbar ? (
        <DataTableToolbar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          showClearAll={showClearAll}
          onClearAll={onClearAll}
          activeFilterCount={activeFilterCount}
          onOpenColumns={() => {
            seedColumnsDraft();
            setColumnsOpen(true);
          }}
          onOpenFilters={() => {
            filterSheet?.onOpen?.();
            setFiltersOpen(true);
          }}
          leadingContent={toolbarLeading}
          extraActions={toolbarActions}
          afterSearch={toolbarAfterSearch}
          className={toolbarClassName}
          searchClassName={searchClassName}
          showSearch={showSearch}
          showFilters={showFilters}
          exporting={false}
          showExport={false}
          onExportXLSX={() => undefined}
          onExportCSV={() => undefined}
        />
      ) : null}

      {filterSheet ? (
        <Sheet
          open={filtersOpen}
          onOpenChange={(open) => {
            if (open) filterSheet.onOpen?.();
            setFiltersOpen(open);
          }}
        >
          <DataTableDrawerSheet
            title={filterSheet.title ?? 'Filters'}
            onClose={() => setFiltersOpen(false)}
            onReset={filterSheet.onReset}
            resetDisabled={filterSheet.resetDisabled}
            onApply={() => {
              filterSheet.onApply?.();
              setFiltersOpen(false);
            }}
            footer="actions"
          >
            {filterSheet.content}
          </DataTableDrawerSheet>
        </Sheet>
      ) : null}

      <Sheet
        open={columnsOpen}
        onOpenChange={(open) => {
          if (open) seedColumnsDraft();
          setColumnsOpen(open);
        }}
      >
        <DataTableDrawerSheet
          title="Columns"
          onClose={() => setColumnsOpen(false)}
          onReset={handleResetColumns}
          onApply={applyColumnsDraft}
          footer="apply"
          scrollBody={false}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnsDragEnd}
          >
            <DataTableColumnVisibilityList
              items={draftOrder}
              columns={columnDrawerColumns}
              visibleKeys={draftVisible}
              onToggle={handleToggleColumn}
            />
          </DndContext>
        </DataTableDrawerSheet>
      </Sheet>

      <div className={flush ? 'min-h-0 flex-1' : 'min-h-0 flex-1 px-4 py-2'}>
        {isError ? (
          <div className="h-64">
            <CollectionErrorState title={errorTitle} onRetry={onRetry} />
          </div>
        ) : emptyState && !isLoading && rows.length === 0 ? (
          emptyState
        ) : (
          <AgGridReact<TRow>
            theme={theme}
            columnDefs={colDefs}
            rowData={gridRows as TRow[]}
            pinnedBottomRowData={pinnedBottomRows}
            rowHeight={rowHeight}
            getRowHeight={
              getRowHeight
                ? (params) =>
                    params.data && !isLoaderRow(params.data) ? getRowHeight(params.data) : undefined
                : undefined
            }
            isFullWidthRow={
              isFullWidthRow || infinite
                ? (params) => {
                    const data = params.rowNode.data;
                    if (!data) return false;
                    if (isLoaderRow(data)) return true;
                    return Boolean(isFullWidthRow?.(data));
                  }
                : undefined
            }
            fullWidthCellRenderer={
              renderFullWidthRow || infinite
                ? (params: ICellRendererParams<TRow>) => {
                    const data = params.data as TRow | LoaderRow | undefined;
                    if (!data) return null;
                    if (isLoaderRow(data)) {
                      return (
                        <DataGridLoaderRow
                          row={data}
                          columnWidths={params.api
                            .getAllDisplayedColumns()
                            .map((column) => column.getActualWidth())}
                          onRetry={infiniteRef.current?.onRetry}
                          failedLabel={infiniteRef.current?.failedLabel ?? "Couldn't load more."}
                        />
                      );
                    }
                    return renderFullWidthRow ? (
                      <div className="h-full w-full">{renderFullWidthRow(data)}</div>
                    ) : null;
                  }
                : undefined
            }
            getRowId={(params) => {
              const data = params.data as TRow | LoaderRow;
              return isLoaderRow(data) ? `__loader-${data.__dataGridLoader}-${data.index}` : getRowKey(data);
            }}
            defaultColDef={defaultColDef}
            sortingOrder={stableSortingOrder}
            maintainColumnOrder
            tooltipShowDelay={400}
            // Infinite lists draw shimmer rows instead of the spinner overlay.
            loading={infinite ? false : isLoading}
            overlayNoRowsTemplate={emptyTitle}
            suppressMovableColumns={false}
            suppressDragLeaveHidesColumns
            onGridReady={handleGridReady}
            onGridSizeChanged={(event) => handleGridSizeChanged(event.api)}
            // The first page of rows often arrives after the grid's initial
            // size event. Reconcile its viewports once the data is installed;
            // otherwise Contacts and Jobs can keep an empty canvas until a
            // drawer changes the available width and triggers a size event.
            onRowDataUpdated={(event) => {
              if (event.api.getDisplayedRowCount() > 0) handleGridSizeChanged(event.api);
            }}
            onSortChanged={handleSortChanged}
            onFilterChanged={handleFilterChanged}
            onColumnMoved={handleColumnMoved}
            onColumnResized={handleColumnResized}
            onColumnVisible={(event) => {
              handleColumnChanged();
              measureOverflow(event.api);
            }}
            onBodyScroll={(event) => {
              if (event.direction === 'horizontal') measureOverflow(event.api);
              else maybeLoadMore(event.api);
            }}
            onFirstDataRendered={(event) => {
              measureOverflow(event.api);
              maybeLoadMore(event.api);
            }}
            onColumnPinned={handleColumnChanged}
            onCellClicked={handleCellClicked}
            rowSelection={selectionConfig}
            onSelectionChanged={handleSelectionChanged}
            rowStyle={onRowClick ? { cursor: 'pointer' } : undefined}
            getRowClass={(params) =>
              params.data &&
              !isLoaderRow(params.data) &&
              activeRowKeyRef.current &&
              getRowKeyRef.current(params.data) === activeRowKeyRef.current
                ? 'data-grid-active-row'
                : undefined
            }
            getRowStyle={
              infinite
                ? (params) => (isLoaderRow(params.data) ? { cursor: 'default' } : undefined)
                : undefined
            }
          />
        )}
      </div>

      {infinite ? (
        !isLoading && rows.length > 0 ? (
          <DataTablePaginationFooter
            count={rows.length}
            singular=""
            countLabel={infinite.countLabel}
            page={1}
            totalPages={1}
            onPrevious={() => undefined}
            onNext={() => undefined}
            showPagination={false}
          />
        ) : null
      ) : !isLoading && pagination && pagination.count > 0 ? (
        <DataTablePaginationFooter
          count={pagination.count}
          singular={pagination.singular}
          plural={pagination.plural}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPrevious={pagination.onPrevious}
          onNext={pagination.onNext}
        />
      ) : null}
    </div>
  );
}
